/**
 * WizardStepVehicles - Passo "Veículos" do wizard de geração de contrato
 *
 * Permite selecionar quais veículos do cliente/contato/negócio serão
 * incluídos no contrato. A seleção alimenta as variáveis {{veiculo_*}} e o
 * loop {{#veiculos}} do template, além do snapshot em contract_vehicles.
 *
 * @version 1.0.0
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (Fase 7 do módulo de Veículos)
 */

import React from 'react';
import { Car } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { VEHICLE_TYPE_LABELS, type CustomerVehicle } from '@/hooks/useCustomerVehicles';

interface WizardStepVehiclesProps {
  /** Veículos disponíveis no escopo (negócio + cliente/contato) */
  vehicles: CustomerVehicle[];
  /** Carregando a listagem */
  isLoading?: boolean;
  /** IDs selecionados */
  selectedIds: string[];
  /** Callback de mudança da seleção */
  onChange: (ids: string[]) => void;
}

/** Formata valor FIPE em BRL */
function formatCurrency(value: number | null): string {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function WizardStepVehicles({
  vehicles,
  isLoading = false,
  selectedIds,
  onChange,
}: WizardStepVehiclesProps) {
  /** Alterna a seleção de um veículo */
  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((v) => v !== id) : [...selectedIds, id]);
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed py-12 text-center">
        <Car className="h-8 w-8 text-muted-foreground/60" />
        <div>
          <p className="text-sm font-medium">Nenhum veículo disponível</p>
          <p className="text-xs text-muted-foreground">
            Cadastre veículos no cliente ou vincule-os ao negócio para incluí-los no contrato.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Selecione os veículos que serão incluídos no contrato. Os dados são congelados no momento
        da geração.
      </p>

      <div className="space-y-2">
        {vehicles.map((vehicle) => {
          const checked = selectedIds.includes(vehicle.id);
          return (
            <button
              key={vehicle.id}
              type="button"
              onClick={() => toggle(vehicle.id)}
              className={cn(
                'w-full flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                checked ? 'border-primary bg-primary/5' : 'hover:bg-muted/40',
              )}
            >
              <Checkbox checked={checked} className="mt-0.5 pointer-events-none" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-medium">{vehicle.plate || 'Sem placa'}</span>
                  <Badge variant="outline" className="font-normal">
                    {vehicle.vehicle_type === 'OTHER'
                      ? vehicle.category_name || 'Outros'
                      : VEHICLE_TYPE_LABELS[vehicle.vehicle_type]}
                  </Badge>
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  {[vehicle.brand_name, vehicle.model_name, vehicle.year, vehicle.color]
                    .filter(Boolean)
                    .join(' · ') || 'Sem detalhes cadastrados'}
                </p>
              </div>
              <span className="shrink-0 text-xs font-medium text-muted-foreground">
                {formatCurrency(vehicle.fipe_value)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
